import _ from 'underscore';

import * as NavConstants from 'constants/NavConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import notifications, {Response} from 'notifications';
import {Thunk} from 'redux-stores/Store';
import {ServerRequestAssignee} from 'requests/assignees/prepareAssigneesForServer';
import {getLegacyRequestObjectName, Request, RequestStatus} from 'requests/RequestConstants';
import * as RequestReduxActions from 'requests/RequestReduxActions';
import {ServerUIRequestTask} from 'requests/tasks/RequestTaskConstants';
import * as RequestTaskReduxActions from 'requests/tasks/RequestTaskReduxActions';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as SearchUIReduxActions from 'search/SearchUIReduxActions';
import {Optional} from 'util/Types';
import * as SampleGroupDAO from 'workflows/samples/SampleGroupDAO';
import * as SampleGroupReduxActions from 'workflows/samples/SampleGroupReduxActions';
import * as StageRunActions from 'workflows/StageRunActions';
import {SampleGroupUpdate} from 'workflows/WorkflowConstants';

import * as InputDAO from './InputDAO';

export enum InputReduxActionType {
  REQUEST_CREATE_STARTED = 'REQUEST_CREATE_STARTED',
  REQUEST_CREATE_COMPLETED = 'REQUEST_CREATE_COMPLETED',
  REQUEST_CREATE_FAILED = 'REQUEST_CREATE_FAILED',
  REQUEST_SAMPLE_GROUPS_FETCH_COMPLETED = 'REQUEST_SAMPLE_GROUPS_FETCH_COMPLETED',
}

export type InputReduxAction =
  | RequestCreateStartedAction
  | RequestCreateCompletedAction
  | RequestCreateFailedAction
  | RequestSampleGroupsFetchCompletedAction;

export interface UpdateRequestArgs {
  assignees?: Array<ServerRequestAssignee>;
  scheduledOn?: string;
  projectId?: string;
  requestStatus?: RequestStatus;
  tags?: TagUpdateObject;
}

export function updateRequest(requestId: string, data: UpdateRequestArgs): Thunk<Promise<void>> {
  return async (dispatch) => {
    await InputDAO.updateRequest(requestId, data).then(
      ({request}: {request: Request}) => {
        dispatch(RequestReduxActions.requestsFetchCompleted([request]));
        notifications.success('Request updated successfully.');
      },
      (resp: Response) => {
        notifications.notifyError(resp);
      }
    );
  };
}

export function bulkUpdateRequests(requestIds: Array<string>, data: UpdateRequestArgs): Thunk<Promise<void>> {
  return async (dispatch) => {
    await InputDAO.bulkUpdateRequests(requestIds, data).then(
      ({requests}: {requests: Array<Request>}) => {
        dispatch(RequestReduxActions.requestsFetchCompleted(requests));
        notifications.success('Requests updated successfully.');
      },
      (resp: Response) => {
        notifications.notifyError(resp);
      }
    );
  };
}

export interface RequestCreateStartedAction {
  type: InputReduxActionType.REQUEST_CREATE_STARTED;
}

function requestCreateStarted(): RequestCreateStartedAction {
  return {
    type: InputReduxActionType.REQUEST_CREATE_STARTED,
  };
}

export interface RequestCreateCompletedAction {
  type: InputReduxActionType.REQUEST_CREATE_COMPLETED;
  request: Request;
}

export function requestCreateCompleted(request: Request): RequestCreateCompletedAction {
  return {
    type: InputReduxActionType.REQUEST_CREATE_COMPLETED,
    request,
  };
}

export interface RequestCreateFailedAction {
  type: InputReduxActionType.REQUEST_CREATE_FAILED;
}

function requestCreateFailed(): RequestCreateFailedAction {
  return {
    type: InputReduxActionType.REQUEST_CREATE_FAILED,
  };
}

export interface CreateRequestArgs {
  assignees: Array<ServerRequestAssignee>;
  projectId?: Optional<string> | null;
  requestorId: Optional<string>;
  schemaId: Optional<string>;
  tags: TagUpdateObject;
  sampleGroupIdsByRequestTaskId: {
    [taskCid: string]: Array<string>;
  };
  sampleGroups: Array<SampleGroupUpdate>;
  requestTasks: Array<ServerUIRequestTask>;
}

export function createRequest({
  assignees,
  schemaId,
  projectId,
  requestorId,
  requestTasks,
  sampleGroupIdsByRequestTaskId,
  sampleGroups,
  tags,
}: CreateRequestArgs): Thunk<Promise<void>> {
  return (dispatch) => {
    dispatch(requestCreateStarted());
    return InputDAO.createRequest({
      assignees,
      schemaId,
      projectId,
      requestorId,
      requestTasks,
      sampleGroupIdsByRequestTaskId,
      sampleGroups,
      tags,
    }).then(
      ({request}: {request: Request}) => {
        dispatch(requestCreateCompleted(request));
        FileBrowserActions.openFile(request.id);
        notifications.success(`${getLegacyRequestObjectName()} successfully created`);
        dispatch(SearchUIReduxActions.forceRefreshSearch(NavConstants.Section.REQUESTS));
      },
      (errorResp: Response) => {
        dispatch(requestCreateFailed());
        // Let the framework handle the error and display the error to the user
        throw errorResp;
      }
    );
  };
}

export interface RequestSampleGroupsFetchCompletedAction {
  type: InputReduxActionType.REQUEST_SAMPLE_GROUPS_FETCH_COMPLETED;
  sampleGroupIdsByRequestId: {[requestId: string]: Array<string>};
}

export function requestSampleGroupsFetchCompleted(sampleGroupIdsByRequestId: {
  [requestId: string]: Array<string>;
}): RequestSampleGroupsFetchCompletedAction {
  return {
    type: InputReduxActionType.REQUEST_SAMPLE_GROUPS_FETCH_COMPLETED,
    sampleGroupIdsByRequestId,
  };
}

export function fetchRequestSampleGroups(requestIds: Array<string>): Thunk<Promise<void>> {
  return async (dispatch) => {
    const {
      sampleGroups,
      sampleGroupIdsByRequestId,
      stageRunSamples,
      batchesById,
      containersById,
      fileSummariesById,
    } = await SampleGroupDAO.fetchSampleGroups({requestIds});
    StageRunActions.dispatchStageRunSamplesFetched({
      stageRunSamples,
      batchesById,
      containersById,
      fileSummariesById,
    });
    dispatch(SampleGroupReduxActions.sampleGroupsFetchCompleted(sampleGroups));
    dispatch(requestSampleGroupsFetchCompleted(sampleGroupIdsByRequestId));
  };
}

export function updateRequestSampleGroups({
  requestId,
  sampleGroupIdsByRequestTaskId,
  sampleGroups,
}: {
  requestId: string;
  sampleGroupIdsByRequestTaskId: {[taskId: string]: Array<string>};
  sampleGroups: Array<SampleGroupUpdate>;
}): Thunk<Promise<void>> {
  return async (dispatch) => {
    const {
      sampleGroupIdsByRequestTaskId: newSampleGroupIdsByRequestTaskId,
      sampleGroups: newSampleGroups,
      stageRunSamples,
      batchesById,
      containersById,
      fileSummariesById,
    } = await InputDAO.updateRequestSampleGroups({requestId, sampleGroupIdsByRequestTaskId, sampleGroups});
    StageRunActions.dispatchStageRunSamplesFetched({
      stageRunSamples,
      batchesById,
      containersById,
      fileSummariesById,
    });
    dispatch(SampleGroupReduxActions.sampleGroupsFetchCompleted(newSampleGroups));
    const sampleGroupIdsByRequestId = {
      // eslint-disable-next-line no-restricted-syntax
      [requestId]: _.pluck(newSampleGroups, 'id'),
    };
    dispatch(requestSampleGroupsFetchCompleted(sampleGroupIdsByRequestId));
    dispatch(
      RequestTaskReduxActions.requestTaskSampleGroupAssociationsUpdated(newSampleGroupIdsByRequestTaskId)
    );
  };
}
